import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { buildPhoneSpecRows, formatUserRating, parsePhoneSpecs, quotePurchase, PAYMENTS_ENABLED, liveMarketSuggestBody, type PhoneSpecRow, type SellerPublicSummary, DEFAULT_LOCALE, isAppLocale, localeFromPathname, pathWithLocale, buildAppPath, formatListingPublishedAt, buildListingShareText, buildListingShareTitle, buildListingShareBody, normalizeListingShareTitle, hasBatteryTransparency, isPlatformVerifiedListing, isListingReserved, reservationBlocksBuyer } from '@redevicer/shared'
import { listingsAPI, offersAPI, paymentsAPI, pricingAPI, sellerVerificationAPI } from '../api/client'
import TrustReportPanel from './TrustReportPanel'
import SellerBadge from './SellerBadge'
import BatteryTransparencyPanel from './BatteryTransparencyPanel'
import PartsAuthenticityPanel from './PartsAuthenticityPanel'
import { useCart } from '../hooks/useCart'
import { useFavorites } from '../hooks/useFavorites'
import OfferModal from './OfferModal'
import ExchangeOfferModal from './ExchangeOfferModal'
import OfferChatPanel from './OfferChatPanel'
import PurchaseRoutePicker, { type BuyChoice } from './PurchaseRoutePicker'
import { buildCheckoutShippingPayload } from '@redevicer/shared'
import ListingFavoriteButton from './ListingFavoriteButton'
import ModalCloseButton from './ModalCloseButton'
import { IconCart } from './AppNavIcons'
import { publicAssetUrl } from '../utils/assetUrl'
import { hasListingPhotos } from '../utils/stockPhoneImage'
import ListingNoPhotoPlaceholder from './ListingNoPhotoPlaceholder'
import ListingCommentsSection from './ListingCommentsSection'
import PriceInsightBadge from './PriceInsightBadge'
import { inBoxLabelKey } from '../utils/inBoxCompleteness'
import { getListingCache, setListingCache } from '../utils/listingDetailCache'
import { blocksOwnListingAction } from '../utils/testDeals'
import { listingCardThumb } from '../utils/listingThumb'
import { applyListingShareMeta, resetListingShareMeta } from '../utils/listingShareMeta'
import LightboxZoomImage from './LightboxZoomImage'
import ListingDefectsSummary from './ListingDefectsSummary'
import ListingDetailPhotoCarousel from './ListingDetailPhotoCarousel'
import ListingDetailLightboxCarousel from './ListingDetailLightboxCarousel'
import { resolveListingDefectMarks, sellerDescriptionExcludingDefects } from '../utils/defectMarks'

function buyNowErrorKey(detail: unknown): string {
  if (typeof detail !== 'string') return 'listing.buyNowFailed'
  if (detail === 'listing_reserved' || detail.includes('listing is reserved')) {
    return 'listing.detailReservedOther'
  }
  if (detail === 'listing_reserved_by_other' || detail.includes('reserved by another buyer')) {
    return 'listing.buyNowReservedByOther'
  }
  if (detail.includes('Cannot buy your own') || detail.includes('own listing')) {
    return 'catalog.cannotOfferOwn'
  }
  if (detail.includes('no longer available') || detail.includes('already sold')) {
    return 'listing.detailSold'
  }
  if (detail === 'seller_connect_required') {
    return 'listing.sellerConnectRequired'
  }
  return 'listing.buyNowFailed'
}

interface ListingImage {
  id: number
  thumbnail_url: string
  display_url: string
  is_primary: boolean
}

interface ListingDetail {
  id: number
  title: string
  description: string | null
  price: number
  condition: string
  battery_health?: number | null
  battery_cycles?: number | null
  battery_original?: boolean | null
  battery_tested_at?: string | null
  parts_authenticity?: Record<string, { status?: string; grade?: string | null }> | null
  photo_quality?: string | null
  photo_suggested_condition?: string | null
  model_match?: string | null
  imei: string
  imei_verified?: boolean
  ai_recommended_eur?: number | null
  catalog_rank_group?: number
  created_at: string
  owner_id: number
  is_sold: boolean
  is_reserved?: boolean
  reserved_for_user_id?: number | null
  release_year?: number | null
  shipment_method?: string
  shipping_included?: boolean
  shipping_cost_eur?: number | null
  seller_city?: string | null
  in_box_completeness?: string
  usage_duration?: string | null
  has_factory_warranty?: boolean
  warranty_remaining?: string | null
  defect_marks?: { x: number; y: number; label: string; photo_index?: number }[]
  phone_model: {
    id?: number
    manufacturer: string
    model_name: string
    hero_image_path?: string | null
    release_year?: number | null
    description?: string | null
    specs_json?: string | null
  }
  phone_model_id?: number
  is_smartphone?: boolean
  images?: ListingImage[]
  seller?: SellerPublicSummary | null
}

interface ListingDetailModalProps {
  listingId: number | null
  onClose: () => void
  token: string | null
  currentUserId?: number
  onRequireLogin?: (intent?: 'buy' | 'offer' | 'exchange' | 'comment') => void
  /** After login — open make-offer modal */
  autoOfferNow?: boolean
  /** After login — open exchange modal */
  autoExchangeNow?: boolean
  onBuyComplete?: () => void
  onGoToAccount?: () => void
}

function maskImei(digits: string | null | undefined): string {
  const d = (digits ?? '').replace(/\D/g, '')
  if (d.length >= 4) return `…${d.slice(-4)}`
  return '—'
}

type ListingHeadlineSource = {
  title: string
  phone_model: { manufacturer: string; model_name: string }
}

function listingHeadline(listing: ListingHeadlineSource): { brand: string; title: string } {
  const manufacturer = listing.phone_model.manufacturer.trim()
  const modelName = listing.phone_model.model_name.trim()
  const modelLine = `${manufacturer} ${modelName}`.trim()
  const title = listing.title.trim()
  const tLower = title.toLowerCase()
  const mLower = modelLine.toLowerCase()

  if (!title || tLower === mLower) {
    return { brand: manufacturer, title: modelName }
  }
  if (tLower.startsWith(mLower)) {
    return { brand: manufacturer, title }
  }
  return { brand: manufacturer, title }
}

function deviceSpecLabel(id: PhoneSpecRow['id'], t: (key: string) => string): string {
  const keys: Record<PhoneSpecRow['id'], string> = {
    chip: 'listing.detailChip',
    screen: 'listing.detailScreen',
    ram: 'listing.detailRam',
    camera: 'listing.detailCamera',
    fiveG: 'listing.detail5g',
    foldable: 'listing.detailFoldable',
  }
  return t(keys[id])
}

export default function ListingDetailModal({
  listingId,
  onClose,
  token,
  currentUserId,
  onRequireLogin,
  autoOfferNow = false,
  autoExchangeNow = false,
  onBuyComplete,
  onGoToAccount,
}: ListingDetailModalProps) {
  const { t, i18n } = useTranslation()
  const { add: addToCart } = useCart()
  const { has: isFavorite, toggle: toggleFavorite } = useFavorites()
  const [listing, setListing] = useState<ListingDetail | null>(null)
  const [liveMarketEur, setLiveMarketEur] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [activePhoto, setActivePhoto] = useState(0)
  const [offerOpen, setOfferOpen] = useState(false)
  const [exchangeOpen, setExchangeOpen] = useState(false)
  const [buyBusy, setBuyBusy] = useState(false)
  const [chatOfferId, setChatOfferId] = useState<number | null>(null)
  const [chatSeedNote, setChatSeedNote] = useState<string | null>(null)
  const [buyChoice, setBuyChoice] = useState<BuyChoice>('peer')
  const [peerContact, setPeerContact] = useState<{
    seller_display_name: string
    contact_phone?: string | null
    contact_email?: string | null
    seller_rating_avg?: number | null
    seller_rating_count?: number
    verification_level?: number
    member_since?: string | null
    completed_deals_count?: number
    is_verified_seller?: boolean
  } | null>(null)
  const [stripeEnabled, setStripeEnabled] = useState<boolean | null>(null)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [lightboxZoomed, setLightboxZoomed] = useState(false)
  const moreDetailsRef = useRef<HTMLDetailsElement>(null)
  const loadGenRef = useRef(0)
  const prevListingIdRef = useRef<number | null>(null)
  const displayIdRef = useRef<number | null>(null)
  const closeSourceRef = useRef<'none' | 'user' | 'route'>('none')
  const [displayId, setDisplayId] = useState<number | null>(null)
  const [closing, setClosing] = useState(false)

  displayIdRef.current = displayId

  const CLOSE_MS = 340

  const requestClose = useCallback(() => {
    if (closing) return
    if (galleryOpen) {
      setGalleryOpen(false)
      return
    }
    closeSourceRef.current = 'user'
    setClosing(true)
  }, [closing, galleryOpen])

  useEffect(() => {
    if (listingId != null) {
      setDisplayId(listingId)
      setClosing(false)
      closeSourceRef.current = 'none'
      return
    }
    if (displayIdRef.current != null && closeSourceRef.current !== 'user') {
      closeSourceRef.current = 'route'
      setClosing(true)
    }
  }, [listingId])

  useEffect(() => {
    if (!closing) return
    const id = window.setTimeout(() => {
      if (closeSourceRef.current === 'user' && listingId != null) {
        onClose()
      }
      closeSourceRef.current = 'none'
      setDisplayId(null)
    }, CLOSE_MS)
    return () => window.clearTimeout(id)
  }, [closing, listingId, onClose])

  useEffect(() => {
    if (displayId == null) {
      setListing(null)
      setError('')
      setActivePhoto(0)
      setOfferOpen(false)
      setGalleryOpen(false)
      setBuyChoice('peer')
      setPeerContact(null)
      setChatOfferId(null)
      setChatSeedNote(null)
      prevListingIdRef.current = null
      return
    }

    if (prevListingIdRef.current !== displayId) {
      setBuyChoice('peer')
      setPeerContact(null)
      setChatOfferId(null)
      setChatSeedNote(null)
      prevListingIdRef.current = displayId
    }

    const gen = ++loadGenRef.current
    setError('')
    setActivePhoto(0)

    const cached = getListingCache(displayId) as ListingDetail | null
    if (cached) {
      setListing(cached)
      setLoading(false)
    } else {
      setListing(null)
      setLoading(true)
    }

    listingsAPI
      .getListing(displayId)
      .then((res) => {
        if (loadGenRef.current !== gen) return
        const data = res.data as ListingDetail
        setListingCache(displayId, data)
        setListing(data)
        setError('')
      })
      .catch(() => {
        if (loadGenRef.current !== gen) return
        if (!cached) setError(t('listing.detailLoadError'))
      })
      .finally(() => {
        if (loadGenRef.current === gen) setLoading(false)
      })
  }, [displayId, t])

  useEffect(() => {
    const n = listing?.images?.length ?? 0
    if (n <= 0) return
    setActivePhoto((i) => (i >= n ? n - 1 : i))
  }, [listing?.images?.length])

  useEffect(() => {
    if (!listing || listing.is_smartphone === false) {
      setLiveMarketEur(null)
      return
    }
    const body = liveMarketSuggestBody(listing)
    if (!body) {
      setLiveMarketEur(null)
      return
    }
    let cancelled = false
    void pricingAPI
      .suggest(body)
      .then((res) => {
        if (cancelled) return
        const rec = (res.data as { recommended_eur?: number })?.recommended_eur
        setLiveMarketEur(typeof rec === 'number' && rec > 0 ? rec : null)
      })
      .catch(() => {
        if (!cancelled) setLiveMarketEur(null)
      })
    return () => {
      cancelled = true
    }
  }, [listing])

  const deviceSpecRows = useMemo(() => {
    if (!listing) return []
    return buildPhoneSpecRows(parsePhoneSpecs(listing.phone_model.specs_json))
  }, [listing])

  const hasExtendedSpecs = useMemo(() => {
    if (!listing) return false
    if (listing.phone_model.description?.trim()) return true
    if (deviceSpecRows.length > 0) return true
    if (listing.usage_duration) return true
    if (listing.imei) return true
    return false
  }, [listing, deviceSpecRows])

  const hasListingExtraSpecs = Boolean(listing?.usage_duration || listing?.imei)

  useEffect(() => {
    if (!listing || !moreDetailsRef.current || !hasExtendedSpecs) return
    const desktop = window.matchMedia('(min-width: 841px)').matches
    moreDetailsRef.current.open = desktop
  }, [listing, hasExtendedSpecs])

  const purchaseQuote = useMemo(
    () =>
      listing
        ? quotePurchase(
            {
              price: listing.price,
              condition: listing.condition,
              shipment_method: listing.shipment_method,
              shipping_included: listing.shipping_included,
              shipping_cost_eur: listing.shipping_cost_eur,
            },
            'guarantee',
          )
        : null,
    [listing],
  )

  const isOwnBlocked =
    listing !== null && blocksOwnListingAction(listing.owner_id, currentUserId)

  const buyerReservedBlocked =
    listing !== null && reservationBlocksBuyer(listing, currentUserId)

  const reservedNotice = useMemo(() => {
    if (!listing || !isListingReserved(listing)) return null
    if (buyerReservedBlocked) return t('listing.detailReservedOther')
    if (listing.reserved_for_user_id === currentUserId) return t('listing.detailReservedForYou')
    return t('listing.detailReserved')
  }, [listing, buyerReservedBlocked, currentUserId, t])

  const headline = useMemo(
    () => (listing ? listingHeadline(listing) : null),
    [listing],
  )

  useEffect(() => {
    if (!listing) {
      if (displayId == null) resetListingShareMeta()
      return
    }
    const loc =
      localeFromPathname(window.location.pathname) ??
      (isAppLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE)
    const url = `${window.location.origin}${pathWithLocale(loc, buildAppPath({ page: 'buy', listingId: listing.id }))}`
    const thumb = listingCardThumb(listing)
    applyListingShareMeta({
      title: normalizeListingShareTitle(listing.title, listing.phone_model.manufacturer),
      price: listing.price,
      manufacturer: listing.phone_model.manufacturer,
      modelName: listing.phone_model.model_name,
      conditionLabel: t(`listing.condition.${listing.condition}`, listing.condition),
      url,
      imageUrl: thumb.src,
    })
    return () => {
      resetListingShareMeta()
    }
  }, [listing, displayId, i18n.language, t])

  useEffect(() => {
    if (!galleryOpen) return
    const meta = document.querySelector('meta[name="viewport"]')
    const prev = meta?.getAttribute('content') ?? ''
    if (meta) {
      meta.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no',
      )
    }
    return () => {
      if (meta && prev) meta.setAttribute('content', prev)
    }
  }, [galleryOpen])

  const scrollLockYRef = useRef(0)

  useEffect(() => {
    if (displayId == null) return

    const scrollY = window.scrollY
    scrollLockYRef.current = scrollY
    const { body } = document
    const html = document.documentElement

    html.classList.add('listing-detail-scroll-lock')
    body.style.top = `-${scrollY}px`

    return () => {
      html.classList.remove('listing-detail-scroll-lock')
      body.style.top = ''
      window.scrollTo(0, scrollLockYRef.current)
    }
  }, [displayId])

  useEffect(() => {
    return () => {
      document.documentElement.classList.remove('listing-detail-scroll-lock')
      document.body.style.top = ''
    }
  }, [])

  useEffect(() => {
    if (!displayId) return
    function onKey(e: KeyboardEvent) {
      if (galleryOpen) {
        const count = listing?.images?.length ?? 0
        if (count > 1) {
          if (e.key === 'ArrowLeft') {
            e.preventDefault()
            setLightboxZoomed(false)
            setActivePhoto((i) => Math.max(0, i - 1))
            return
          }
          if (e.key === 'ArrowRight') {
            e.preventDefault()
            setLightboxZoomed(false)
            setActivePhoto((i) => Math.min(count - 1, i + 1))
            return
          }
        }
      }
      if (e.key !== 'Escape') return
      if (galleryOpen) {
        setGalleryOpen(false)
        return
      }
      requestClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [displayId, requestClose, galleryOpen, listing?.images?.length])

  useEffect(() => {
    if (!PAYMENTS_ENABLED) return
    paymentsAPI
      .getPublicConfig()
      .then((res) => setStripeEnabled(!!res.data?.stripe_checkout_enabled))
      .catch(() => setStripeEnabled(false))
  }, [])

  const runBuyNow = useCallback(() => {
    if (!token || !listing || buyBusy || buyChoice !== 'guarantee') return
    setBuyBusy(true)
    sellerVerificationAPI
      .getStatus(token)
      .then((verifyRes) => {
        if (!verifyRes.data?.can_sell) {
          const go = window.confirm(`${t('platformVerify.required')}\n\n${t('sellerVerify.goToAccount')}?`)
          if (go) onGoToAccount?.()
          return Promise.reject(new Error('verify'))
        }
        return listingsAPI.buyNow(
          listing.id,
          token,
          'guarantee',
          buildCheckoutShippingPayload({}),
        )
      })
      .then((res) => {
        if (!res) return
        const url = res.data?.checkout_url as string | undefined
        if (url) {
          window.location.href = url
          return
        }
        alert(res.data?.message || t('listing.buyNowReserved'))
        onBuyComplete?.()
        requestClose()
      })
      .catch((err) => {
        if (err instanceof Error && err.message === 'verify') return
        const detail = err.response?.data?.detail
        const key = buyNowErrorKey(detail)
        const msg =
          key === 'listing.buyNowFailed' && typeof detail === 'string' && detail.length < 200
            ? detail
            : t(key)
        alert(msg)
      })
      .finally(() => setBuyBusy(false))
  }, [token, listing, buyBusy, buyChoice, t, onBuyComplete, requestClose, onGoToAccount])

  const revealPeerContact = useCallback(async () => {
    if (!token || !listing || buyBusy) return
    setBuyBusy(true)
    try {
      const res = await listingsAPI.peerContact(listing.id, token)
      setPeerContact(res.data)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(typeof detail === 'string' ? detail : t('peerContact.failed'))
    } finally {
      setBuyBusy(false)
    }
  }, [token, listing, buyBusy, t])

  useEffect(() => {
    if (!autoOfferNow || !token || !listing || listing.is_sold) return
    if (blocksOwnListingAction(listing.owner_id, currentUserId)) return
    setOfferOpen(true)
  }, [autoOfferNow, token, listing, currentUserId])

  useEffect(() => {
    if (!autoExchangeNow || !token || !listing || listing.is_sold) return
    if (blocksOwnListingAction(listing.owner_id, currentUserId)) return
    setExchangeOpen(true)
  }, [autoExchangeNow, token, listing, currentUserId])

  if (displayId == null) return null

  const photos = listing?.images?.length
    ? [...listing.images].sort((a, b) => {
        if (a.is_primary && !b.is_primary) return -1
        if (!a.is_primary && b.is_primary) return 1
        return a.id - b.id
      })
    : []

  const hasUserPhotos = Boolean(listing && hasListingPhotos(listing.images))
  const photoDefectMarks = listing?.defect_marks ?? []
  const defectMarksForSummary = listing
    ? resolveListingDefectMarks(listing.defect_marks, listing.description)
    : []
  const sellerAboutText = listing ? sellerDescriptionExcludingDefects(listing.description) : ''
  const safeActivePhoto =
    photos.length > 0 ? Math.max(0, Math.min(photos.length - 1, activePhoto)) : 0
  const mainSrc = listing
    ? photos.length > 0
      ? publicAssetUrl(
          photos[safeActivePhoto].display_url || photos[safeActivePhoto].thumbnail_url,
        )
      : null
    : null

  const gallerySources =
    photos.length > 0
      ? photos.map((img) => publicAssetUrl(img.display_url || img.thumbnail_url))
      : mainSrc
        ? [mainSrc]
        : []
  const lightboxSrc = gallerySources[safeActivePhoto] ?? mainSrc

  function shareListing() {
    if (!listing) return
    const loc =
      localeFromPathname(window.location.pathname) ??
      (isAppLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE)
    const url = `${window.location.origin}${pathWithLocale(loc, buildAppPath({ page: 'buy', listingId: listing.id }))}`
    const shareInput = {
      title: listing.title,
      price: listing.price,
      manufacturer: listing.phone_model.manufacturer,
      modelName: listing.phone_model.model_name,
      conditionLabel: t(`listing.condition.${listing.condition}`, listing.condition),
    }
    const displayTitle = normalizeListingShareTitle(listing.title, listing.phone_model.manufacturer)
    const shareTitle = buildListingShareTitle(displayTitle)
    const shareBody = buildListingShareBody(shareInput)
    const shareText = buildListingShareText({ ...shareInput, url })
    if (navigator.share) {
      void navigator.share({ title: shareTitle, text: shareBody, url }).catch(() => {
        void navigator.clipboard
          .writeText(shareText)
          .then(() => alert(t('listing.shareCopied')))
          .catch(() => prompt(t('listing.share'), shareText))
      })
      return
    }
    navigator.clipboard
      .writeText(shareText)
      .then(() => alert(t('listing.shareCopied')))
      .catch(() => prompt(t('listing.share'), shareText))
  }

  function openOffer() {
    if (!token) {
      onRequireLogin?.('offer')
      return
    }
    if (buyerReservedBlocked) return
    setOfferOpen(true)
  }

  function openExchange() {
    if (!token) {
      onRequireLogin?.('exchange')
      return
    }
    if (buyerReservedBlocked) return
    setExchangeOpen(true)
  }

  async function requestPhotos() {
    if (!listing) return
    if (!token) {
      onRequireLogin?.('offer')
      return
    }
    if (buyBusy || isOwnBlocked || listing.is_sold || buyerReservedBlocked) return

    const photoMsg = t('listing.requestPhotosMessage')
    setBuyBusy(true)
    try {
      const res = await offersAPI.getMyOffers(token, 0, 100)
      const rows = (Array.isArray(res.data) ? res.data : []) as Array<{
        id: number
        listing_id: number
        is_rejected: boolean
      }>
      const existing = rows.find((o) => o.listing_id === listing.id && !o.is_rejected)
      if (existing) {
        await offersAPI.sendOfferMessage(existing.id, photoMsg, token)
        setChatOfferId(existing.id)
        setChatSeedNote(null)
      } else {
        const created = await offersAPI.createOffer(
          {
            listing_id: listing.id,
            offered_price: listing.price,
            message: photoMsg,
          },
          token,
        )
        const offerId = (created.data as { id: number }).id
        setChatOfferId(offerId)
        setChatSeedNote(photoMsg)
      }
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(
        typeof detail === 'string'
          ? t(buyNowErrorKey(detail), { defaultValue: detail })
          : t('listing.requestPhotosFailed'),
      )
    } finally {
      setBuyBusy(false)
    }
  }

  function primaryBuyAction() {
    if (!token) {
      onRequireLogin?.('buy')
      return
    }
    if (buyerReservedBlocked) return
    void revealPeerContact()
  }

  function openGallery(index?: number) {
    if (index != null) setActivePhoto(index)
    if (!mainSrc && photos.length === 0) return
    setGalleryOpen(true)
  }

  function goLightboxPhoto(next: number) {
    if (gallerySources.length === 0) return
    const clamped = Math.max(0, Math.min(gallerySources.length - 1, next))
    if (clamped !== activePhoto) {
      setLightboxZoomed(false)
      setActivePhoto(clamped)
    }
  }

  function putInCart() {
    if (!listing) return
    const line = { listing_id: listing.id, owner_id: listing.owner_id }
    const result = addToCart(line)
    if (result === 'invalid') {
      alert(t('cart.addFailed'))
      return
    }
    if (result === 'duplicate') {
      alert(t('cart.alreadyIn'))
      return
    }
    alert(t('cart.added'))
  }

  return (
    <>
      <div
        className={`listing-detail-overlay listing-detail-overlay--app${closing ? ' listing-detail-overlay--closing' : ''}`}
        role="presentation"
        onClick={requestClose}
      >
        <div
          className={`listing-detail-modal listing-detail-modal--app${closing ? ' listing-detail-modal--closing' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="listing-detail-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="listing-detail-modal__glow" aria-hidden="true" />
          <ModalCloseButton
            className="listing-detail-close"
            onClick={requestClose}
            aria-label={t('listing.closeGallery')}
          />

          {loading && !listing && (
            <div className="listing-detail-skeleton" aria-busy="true">
              <div className="listing-detail-skeleton-gallery" />
              <div className="listing-detail-skeleton-body">
                <span className="listing-detail-skeleton-line listing-detail-skeleton-line--short" />
                <span className="listing-detail-skeleton-line" />
                <span className="listing-detail-skeleton-line listing-detail-skeleton-line--price" />
              </div>
            </div>
          )}
          {error && <p className="listing-detail-error">{error}</p>}

          {!loading && !error && listing && (
            <>
              <div className="listing-detail-layout">
                <div className="listing-detail-col listing-detail-col--media">
                <div
                  className={`listing-detail-gallery${
                    hasUserPhotos && photos.length > 0 ? ' listing-detail-gallery--user' : ''
                  }`}
                >
                  {photos.length > 0 ? (
                    <ListingDetailPhotoCarousel
                      key={displayId ?? 'listing'}
                      photos={photos}
                      activePhoto={safeActivePhoto}
                      onActivePhotoChange={(idx) => {
                        const max = photos.length - 1
                        setActivePhoto(max < 0 ? 0 : Math.max(0, Math.min(max, idx)))
                      }}
                      onPhotoClick={(idx) => openGallery(idx)}
                      defectMarks={photoDefectMarks}
                      enlargeHint={t('listing.tapToEnlarge')}
                    />
                  ) : (
                    <div className="listing-detail-main-photo">
                      <div className="listing-detail-no-photo-wrap">
                        <ListingNoPhotoPlaceholder variant="detail" />
                        {!listing.is_sold && !isOwnBlocked ? (
                          <div className="listing-detail-no-photo-actions">
                            <p className="listing-detail-no-photo-actions__hint">
                              {t('listing.requestPhotosHint')}
                            </p>
                            <button
                              type="button"
                              className="listing-detail-request-photos-btn"
                              disabled={buyBusy}
                              onClick={() => void requestPhotos()}
                            >
                              {buyBusy ? '…' : t('listing.requestPhotos')}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                  {photos.length > 1 && (
                    <div
                      className="listing-detail-thumbs"
                      role="tablist"
                      aria-label={t('listing.detailGallery')}
                    >
                      {photos.map((img, idx) => (
                        <button
                          key={img.id}
                          type="button"
                          role="tab"
                          aria-selected={idx === activePhoto}
                          className={`listing-detail-thumb${
                            idx === activePhoto ? ' listing-detail-thumb--active' : ''
                          }`}
                          onClick={() => setActivePhoto(idx)}
                        >
                          <img
                            src={publicAssetUrl(img.display_url || img.thumbnail_url)}
                            alt=""
                            draggable={false}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

              <div className="listing-detail-actions listing-detail-actions--primary listing-detail-actions--simple">
                {listing.is_sold ? (
                  <p className="listing-detail-sold">{t('listing.detailSold')}</p>
                ) : buyerReservedBlocked && !isOwnBlocked ? (
                  <p className="listing-detail-reserved">{reservedNotice}</p>
                ) : (
                  <>
                    {reservedNotice && !isOwnBlocked ? (
                      <p className="listing-detail-reserved listing-detail-reserved--info">{reservedNotice}</p>
                    ) : null}
                    {!isOwnBlocked && PAYMENTS_ENABLED ? (
                      <PurchaseRoutePicker
                        listing={{
                          price: listing.price,
                          condition: listing.condition,
                          shipment_method: listing.shipment_method,
                          shipping_included: listing.shipping_included,
                          shipping_cost_eur: listing.shipping_cost_eur,
                        }}
                        choice={buyChoice}
                        onChoiceChange={(next) => {
                          setBuyChoice(next)
                          setPeerContact(null)
                        }}
                        disabled={buyBusy || buyerReservedBlocked}
                      />
                    ) : null}
                    <div className="listing-detail-actions-cta">
                    {listing.seller && !peerContact ? (
                      <SellerBadge
                        seller={listing.seller}
                        compact
                        className="listing-detail-actions-seller"
                      />
                    ) : null}
                    {!isOwnBlocked ? (
                      <div className="listing-detail-action-bar" role="group">
                        <button
                          type="button"
                          className="listing-detail-action-bar__btn listing-detail-action-bar__btn--primary"
                          disabled={buyBusy || buyerReservedBlocked}
                          onClick={primaryBuyAction}
                        >
                          {buyBusy
                            ? '…'
                            : PAYMENTS_ENABLED && buyChoice === 'guarantee'
                              ? t('catalog.buyNowWithPrice', {
                                  price: `€${(purchaseQuote?.buyerTotalEur ?? listing.price).toFixed(2)}`,
                                })
                              : t('purchase.contactSeller')}
                        </button>
                        <button
                          type="button"
                          className="listing-detail-action-bar__btn"
                          disabled={buyBusy || buyerReservedBlocked}
                          onClick={openOffer}
                        >
                          {t('catalog.offerShort')}
                        </button>
                        {!isOwnBlocked && PAYMENTS_ENABLED ? (
                          <button
                            type="button"
                            className="listing-detail-action-bar__btn"
                            disabled={buyBusy || buyerReservedBlocked}
                            onClick={putInCart}
                          >
                            <IconCart width={15} height={15} aria-hidden="true" />
                            <span>{t('cart.addToCart')}</span>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="listing-detail-action-bar__btn"
                          disabled={buyBusy || buyerReservedBlocked}
                          onClick={openExchange}
                        >
                          {t('catalog.exchange')}
                        </button>
                      </div>
                    ) : null}
                    {peerContact ? (
                      <div className="peer-contact-inline">
                        {peerContact.verification_level != null ? (
                          <SellerBadge
                            compact
                            seller={{
                              verification_level: peerContact.verification_level,
                              display_name: peerContact.seller_display_name,
                              member_since: peerContact.member_since,
                              completed_deals_count: peerContact.completed_deals_count ?? 0,
                              rating_avg: peerContact.seller_rating_avg,
                              rating_count: peerContact.seller_rating_count ?? 0,
                              is_verified_seller: peerContact.is_verified_seller ?? peerContact.verification_level >= 2,
                            }}
                          />
                        ) : (
                          <p>
                            {t('peerContact.sellerLabel')}: <strong>{peerContact.seller_display_name}</strong>
                          </p>
                        )}
                        {peerContact.verification_level == null &&
                        formatUserRating(peerContact.seller_rating_avg, peerContact.seller_rating_count) ? (
                          <p className="seller-rating-inline">
                            {t('peerContact.sellerRating')}:{' '}
                            <strong>
                              {formatUserRating(
                                peerContact.seller_rating_avg,
                                peerContact.seller_rating_count,
                              )}
                            </strong>
                          </p>
                        ) : null}
                        {peerContact.contact_phone ? (
                          <p>
                            <a href={`tel:${peerContact.contact_phone}`}>{peerContact.contact_phone}</a>
                          </p>
                        ) : null}
                        {peerContact.contact_email ? (
                          <p>
                            <a href={`mailto:${peerContact.contact_email}`}>{peerContact.contact_email}</a>
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {chatOfferId && token ? (
                      <OfferChatPanel
                        offerId={chatOfferId}
                        token={token}
                        role="buyer"
                        seedNote={chatSeedNote}
                      />
                    ) : null}
                    {isOwnBlocked && (
                      <p className="listing-detail-own">{t('catalog.cannotOfferOwn')}</p>
                    )}
                    {PAYMENTS_ENABLED && stripeEnabled === false && !isOwnBlocked && buyChoice === 'guarantee' && (
                      <p className="meta-text listing-detail-stripe-hint">
                        {t('listing.buyNowStripeOff')}
                      </p>
                    )}
                    </div>
                  </>
                )}
                <ListingCommentsSection
                  listingId={listing.id}
                  token={token}
                  onRequireLogin={() => onRequireLogin?.('comment')}
                />
              </div>
                </div>

                <div className="listing-detail-col listing-detail-col--info">
                <div className="listing-detail-intro">
              {isListingReserved(listing) ? (
                <span className="reserved-badge">{t('listing.reservedBadge')}</span>
              ) : null}
              {headline ? (
                <>
              <p className="listing-detail-brand">{headline.brand}</p>
              <div className="listing-detail-title-row">
                <h2 id="listing-detail-title" className="listing-detail-title">
                  {headline.title}
                </h2>
                <div className="listing-detail-title-actions">
                  {token ? (
                    <ListingFavoriteButton
                      active={isFavorite(listing.id)}
                      label={
                        isFavorite(listing.id) ? t('vc.favorites.remove') : t('vc.favorites.add')
                      }
                      onClick={() => toggleFavorite(listing.id)}
                    />
                  ) : null}
                  <button
                    type="button"
                    className="listing-detail-share-btn"
                    onClick={shareListing}
                    aria-label={t('listing.share')}
                  >
                    {t('listing.share')}
                  </button>
                </div>
              </div>
                </>
              ) : null}
              <div className="listing-detail-price-row">
                <p className="listing-detail-price">€{listing.price.toFixed(2)}</p>
                <PriceInsightBadge
                  listing={listing}
                  marketEurOverride={liveMarketEur}
                  variant="detail"
                  showMarket
                />
              </div>
                </div>

                <div className="listing-detail-body">
              <div className="listing-detail-main-info-grid">
              <p
                className={`listing-detail-declared-kicker seller-declared-kicker${
                  isPlatformVerifiedListing(listing)
                    ? ' seller-declared-kicker--verified'
                    : ''
                }`}
              >
                {t(
                  isPlatformVerifiedListing(listing)
                    ? 'trust.report.verifiedBadge'
                    : 'batteryTransparency.kicker',
                )}
              </p>
              <BatteryTransparencyPanel
                showKicker={false}
                listing={{
                  battery_health: listing.battery_health,
                  battery_cycles: listing.battery_cycles,
                  battery_original: listing.battery_original,
                  battery_tested_at: listing.battery_tested_at,
                  manufacturer: listing.phone_model.manufacturer,
                }}
              />

              <PartsAuthenticityPanel
                showKicker={false}
                parts={listing.parts_authenticity}
                batteryOriginal={listing.battery_original}
                manufacturer={listing.phone_model.manufacturer}
                modelName={listing.phone_model.model_name}
              />
              </div>

              <TrustReportPanel
                listing={listing}
                loggedIn={Boolean(token)}
                marketEurOverride={liveMarketEur}
                onRequireLogin={token ? undefined : () => onRequireLogin?.()}
              />

              <div className="listing-detail-metadata-row">
              <section className="listing-detail-spec-summary" aria-label={t('listing.detailDeviceInfo')}>
                <dl className="listing-detail-specs">
                  {listing.created_at ? (
                    <div>
                      <dt>{t('listing.publishedAt')}</dt>
                      <dd>
                        <time dateTime={listing.created_at}>
                          {formatListingPublishedAt(listing.created_at, i18n.language)}
                        </time>
                      </dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>{t('listing.detailCondition')}</dt>
                    <dd>{t(`listing.condition.${listing.condition}`, listing.condition)}</dd>
                  </div>
                  {listing.in_box_completeness && (
                    <div>
                      <dt>{t('listing.detailKit')}</dt>
                      <dd>{t(inBoxLabelKey(listing.in_box_completeness))}</dd>
                    </div>
                  )}
                  <div>
                    <dt>{t('listing.warrantyLegend')}</dt>
                    <dd>
                      {listing.has_factory_warranty
                        ? listing.warranty_remaining
                          ? t('listing.warrantyRemainingBadge', {
                              left: t(
                                `listing.warrantyRemaining.${listing.warranty_remaining}`,
                                listing.warranty_remaining,
                              ),
                            })
                          : t('listing.warrantyBadgeYes')
                        : t('listing.warrantyBadgeNo')}
                    </dd>
                  </div>
                  {listing.release_year != null ? (
                    <div>
                      <dt>{t('listing.releaseYear')}</dt>
                      <dd>{listing.release_year}</dd>
                    </div>
                  ) : null}
                </dl>
              </section>

              {listing.shipment_method !== 'redevicer_warehouse' ? (
                <p className="listing-detail-no-guarantee">{t('listing.noGuarantee')}</p>
              ) : null}
              </div>

              {hasExtendedSpecs ? (
              <details ref={moreDetailsRef} className="listing-detail-more ui-collapse-details">
                <summary>{t('listing.detailTechSpecs')}</summary>
                <div className="ui-collapse-details__panel">
                <div className="ui-collapse-details__inner listing-detail-more-inner">
                  {listing.phone_model.description?.trim() ? (
                    <section className="listing-detail-model-desc">
                      <h3>{t('listing.detailModelDesc')}</h3>
                      <p>{listing.phone_model.description}</p>
                    </section>
                  ) : null}
                  {deviceSpecRows.length > 0 ? (
                    <>
                      <h3 className="listing-detail-specs-heading">{t('listing.detailDeviceInfo')}</h3>
                      <dl className="listing-detail-specs listing-detail-specs--device">
                        {deviceSpecRows.map((row) => (
                          <div key={row.id}>
                            <dt>{deviceSpecLabel(row.id, t)}</dt>
                            <dd>{row.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </>
                  ) : null}
                  {hasListingExtraSpecs ? (
                  <dl className="listing-detail-specs">
                    {listing.usage_duration ? (
                      <div>
                        <dt>{t('listing.usageLegend')}</dt>
                        <dd>{t(`listing.usage.${listing.usage_duration}`, listing.usage_duration)}</dd>
                      </div>
                    ) : null}
                    {listing.imei ? (
                      <div>
                        <dt>{t('listing.detailImei')}</dt>
                        <dd>{maskImei(listing.imei)}</dd>
                      </div>
                    ) : null}
                  </dl>
                  ) : null}
                </div>
                </div>
              </details>
              ) : null}

              {sellerAboutText ? (
                <section className="listing-detail-desc listing-detail-desc--top">
                  <h3>{t('listing.detailAbout')}</h3>
                  <p>{sellerAboutText}</p>
                </section>
              ) : null}
              {defectMarksForSummary.length > 0 ? (
                <ListingDefectsSummary
                  marks={defectMarksForSummary}
                  photoCount={photos.length}
                  activePhoto={activePhoto}
                  onSelectPhoto={setActivePhoto}
                  className="listing-detail-defects-summary--in-body"
                />
              ) : null}
                </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {galleryOpen && listing && gallerySources.length > 0 && (
        <div
          className="listing-detail-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={t('listing.detailGallery')}
          onClick={() => setGalleryOpen(false)}
        >
          <ModalCloseButton
            className="listing-detail-lightbox-close"
            variant="dark"
            onClick={(e) => {
              e.stopPropagation()
              setGalleryOpen(false)
            }}
            aria-label={t('listing.closeGallery')}
          />
          {gallerySources.length > 1 ? (
            <div className="listing-detail-lightbox-stage" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="listing-detail-photo-carousel__nav listing-detail-lightbox-arrow listing-detail-lightbox-arrow--prev"
                disabled={activePhoto <= 0}
                aria-label={t('listing.galleryPrev')}
                onClick={(e) => {
                  e.stopPropagation()
                  goLightboxPhoto(activePhoto - 1)
                }}
              >
                <svg className="listing-detail-photo-carousel__nav-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M14.5 6.5 9 12l5.5 5.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <ListingDetailLightboxCarousel
                sources={gallerySources}
                activePhoto={activePhoto}
                onActivePhotoChange={goLightboxPhoto}
                onZoomChange={setLightboxZoomed}
                interactionLocked={lightboxZoomed}
                defectMarks={photoDefectMarks}
              />
              <button
                type="button"
                className="listing-detail-photo-carousel__nav listing-detail-lightbox-arrow listing-detail-lightbox-arrow--next"
                disabled={activePhoto >= gallerySources.length - 1}
                aria-label={t('listing.galleryNext')}
                onClick={(e) => {
                  e.stopPropagation()
                  goLightboxPhoto(activePhoto + 1)
                }}
              >
                <svg className="listing-detail-photo-carousel__nav-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M9.5 6.5 15 12l-5.5 5.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <div
              className="listing-detail-lightbox-stage"
              onClick={(e) => e.stopPropagation()}
            >
              <LightboxZoomImage
                photoKey={lightboxSrc}
                src={lightboxSrc}
                onZoomChange={setLightboxZoomed}
                className="listing-detail-lightbox-img"
                defectPins={photoDefectMarks.filter((m) => (m.photo_index ?? 0) === activePhoto)}
                allDefectMarks={photoDefectMarks}
              />
            </div>
          )}
          {photos.length > 1 && (
            <div
              className="listing-detail-lightbox-thumbs"
              role="tablist"
              onClick={(e) => e.stopPropagation()}
            >
              {photos.map((img, idx) => (
                <button
                  key={img.id}
                  type="button"
                  role="tab"
                  aria-selected={idx === activePhoto}
                  className={`listing-detail-thumb${
                    idx === activePhoto ? ' listing-detail-thumb--active' : ''
                  }`}
                  onClick={() => goLightboxPhoto(idx)}
                >
                  <img
                    src={publicAssetUrl(img.display_url || img.thumbnail_url)}
                    alt=""
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {listing && token && (
        <OfferModal
          listingId={listing.id}
          currentPrice={listing.price}
          token={token}
          isOpen={offerOpen}
          onClose={() => setOfferOpen(false)}
          overlayClassName="modal-overlay--stack-top"
          onSubmitted={() => {
            requestClose()
            onBuyComplete?.()
          }}
        />
      )}
      {listing && token && (
        <ExchangeOfferModal
          listingId={listing.id}
          currentPrice={listing.price}
          token={token}
          isOpen={exchangeOpen}
          onClose={() => setExchangeOpen(false)}
          overlayClassName="modal-overlay--stack-top"
          onSubmitted={() => {
            setExchangeOpen(false)
          }}
          onGoToAccount={onGoToAccount}
        />
      )}
    </>
  )
}
